// FILE: src/auth/permissions.js
// Staff permissions on the client (RBAC). The signed-in user carries
// `permissions` (and `staffRole: { id, name }`) from sign-in and GET
// /api/auth/me; the server checks every request, so these checks only hide
// or explain what the server would refuse (403 PERMISSION_DENIED).
//
// A need is a permission name ("support.reply"), a list that must all be
// held (["support.reply", "support.reply_content"]) or { anyOf: [...] }.
// Students hold none. A staff session from before roles existed (no
// `permissions` yet) is treated as Administrator until /api/auth/me answers,
// as the server does for staff without a role.

// The server's catalogue (src/utils/permissions.js in the backend), in its order.
export const ALL_PERMISSIONS = Object.freeze([
  "dashboard.view",
  "students.view",
  "students.invite",
  "students.edit",
  "students.lock",
  "students.untimed",
  "records.view",
  "records.export",
  "audit.view",
  "support.view",
  "support.reply",
  "support.reply_content",
  "support.log",
  "support.report",
  "accommodations.manage",
  "survey.view",
  "content.view",
  "content.edit",
  "curricula.manage",
  "curricula.approve",
  "access_links.manage",
  "staff.manage",
  "roles.manage"
]);

// Short names, as the role editor shows them (audit details, "no access" notes).
export const PERMISSION_LABELS = Object.freeze({
  "dashboard.view": "See the dashboard",
  "students.view": "See students",
  "students.invite": "Invite students",
  "students.edit": "Change student details",
  "students.lock": "Lock and unlock student accounts",
  "students.untimed": "Grant or remove untimed access",
  "records.view": "See compliance records",
  "records.export": "Download records and exports",
  "audit.view": "See the audit log",
  "support.view": "See support requests",
  "support.reply": "Answer support requests",
  "support.reply_content": "Answer course-content questions",
  "support.log": "Log phone and email requests",
  "support.report": "See the service-level report",
  "accommodations.manage": "Handle accommodation requests",
  "survey.view": "See course survey responses",
  "content.view": "See lessons and curricula",
  "content.edit": "Change lessons",
  "curricula.manage": "Manage curricula",
  "curricula.approve": "Approve curricula",
  "access_links.manage": "Manage reviewer access links",
  "staff.manage": "Manage staff accounts",
  "roles.manage": "Manage roles"
});

/** A permission in words ("Answer course-content questions"). */
export function permissionLabel(key) {
  return PERMISSION_LABELS[key] || "A permission that no longer exists";
}

/** The permissions held: an array, or null when not known yet (see above). */
export function permissionsOf(auth) {
  const user = auth?.user;
  if (!user || (auth.role || user.role) !== "admin") return [];
  return Array.isArray(user.permissions) ? user.permissions : null;
}

/** True when `need` is met (see the header for the forms a need takes). */
export function canDo(auth, need) {
  if (!auth?.user || (auth.role || auth.user.role) !== "admin") return false;
  if (!need) return true;
  const held = permissionsOf(auth);
  if (held === null) return true;
  if (typeof need === "string") return held.includes(need);
  if (Array.isArray(need)) return need.every((p) => held.includes(p));
  if (Array.isArray(need.anyOf)) return need.anyOf.some((p) => held.includes(p));
  return false;
}
